import {CheckIcon, CopyIcon} from '@primer/octicons-react'
import {useEffect, useRef, useState} from 'react'

import {copyText} from './copy'

const copyConfirmationMsDelay = 2000

export interface UseCopyToClipboardOptions<Visual = typeof CopyIcon> {
  /**
   * Optional callback that is invoked after copying is initiated.
   */
  onCopy?: () => void
  /**
   * Text that will be copied to the clipboard.
   */
  textToCopy: string
  /**
   * Visual displayed before the copy action succeeds.
   * @default CopyIcon
   */
  visual?: Visual
}

export function useCopyToClipboard<Visual = typeof CopyIcon>({
  onCopy,
  textToCopy,
  visual,
}: UseCopyToClipboardOptions<Visual>) {
  const [copied, setCopied] = useState(false)
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current !== undefined) clearTimeout(timeoutIdRef.current)
    }
  }, [])

  const copyToClipboard = () => {
    setCopied(true)
    void copyText(textToCopy)
    onCopy?.()

    if (timeoutIdRef.current !== undefined) clearTimeout(timeoutIdRef.current)
    timeoutIdRef.current = setTimeout(() => {
      setCopied(false)
      timeoutIdRef.current = undefined
    }, copyConfirmationMsDelay)
  }

  return {
    announcement: copied ? 'Copied!' : undefined,
    copied,
    copyToClipboard,
    visual: copied ? CheckIcon : (visual ?? CopyIcon),
  }
}
